import { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import AccentRule from './AccentRule';

// ----------------------------------------------------------------------

// The standard page heading for any view that does NOT need a back button:
// the brand accent rule + an h3 title + optional subtitle, with an optional
// right-aligned action. Matches HeaderBreadcrumbs (list pages) and
// PageBackHeader (detail pages) so every page title reads the same — same
// size, same accent bar, same subtitle treatment. Use this instead of a bare
// <Typography variant="h4"> page title.

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned controls. Wraps below the title on mobile. */
  action?: ReactNode;
  /** Semantic element for the title (defaults to h2). */
  titleComponent?: React.ElementType;
  sx?: SxProps<Theme>;
};

export default function PageHeading({ title, subtitle, action, titleComponent, sx }: Props) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
      justifyContent="space-between"
      sx={{ mb: 3, ...sx }}
    >
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
        <AccentRule />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" component={titleComponent || 'h2'} sx={{ lineHeight: 1.15 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              component="div"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Stack>
  );
}
